import React, { FC, useEffect, useState, useMemo } from 'react';

import { getScriptUrl, getPakmsKey } from 'utils/Tokenizer';
import { useScript } from '../../useScript';
import { useSafeContext } from '../../useSafeContext/useSafeContext';

export type ContextValues = {
  cloverInstance?: Clover;
  cloverElementsInstance?: Clover.ElementRoot;
  isScriptLoading: boolean;
  scriptHasError: boolean;
  setLocale: (locale: string) => void;
  locale?: string;
  setMerchantUuid: (merchantUuid: string) => void;
  merchantUuid?: string;
};

const ENV = process.env.NODE_ENV;

const CloverPaymentIframeContext = React.createContext<ContextValues | undefined>(undefined);

export const CloverPaymentIframeProvider: FC = ({ children }) => {
  const [cloverInstance, setCloverInstance] = useState<Clover | undefined>();
  const [locale, setLocale] = useState<string>();
  const [merchantUuid, setMerchantUuid] = useState<string>();

  const cloverElementsInstance = useMemo(() => cloverInstance?.elements(), [cloverInstance]);

  const [scriptLoaded, scriptError] = useScript(getScriptUrl(ENV));

  useEffect(() => {
    if (!scriptLoaded || !locale || !merchantUuid) return;

    // If the locale is fr-CA set it to en-CA until we have a lang picker.
    setCloverInstance(instance => instance ?? new Clover(
        getPakmsKey(ENV),
        {
          locale: locale.includes('CA') ? 'en-CA' : 'en-US',
          merchantId: merchantUuid,
        }
      )
    );
  }, [scriptLoaded, locale, merchantUuid]);

  const values = {
    cloverInstance,
    cloverElementsInstance,
    isScriptLoading: !scriptLoaded,
    scriptHasError: !!scriptError,
    setLocale,
    locale,
    setMerchantUuid,
    merchantUuid,
  };

  return (
    <CloverPaymentIframeContext.Provider value={values}>
      {children}
    </CloverPaymentIframeContext.Provider>
  );
};

// eslint-disable-next-line
CloverPaymentIframeContext.displayName = 'CloverPaymentIframeContext';
export function useCloverPaymentIframeContext() {
  return useSafeContext(useCloverPaymentIframeContext , CloverPaymentIframeContext);
}
